import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Shield as ShieldIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { updateProfile } from '../../store/authSlice';
import apiService from '../../services/api';

interface ProfileFormData {
  name: string;
  email: string;
  bio?: string;
  phone?: string;
  avatar_url?: string;
}

const ProfileSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading } = useSelector((state: RootState) => state.auth);
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    email: '',
    bio: '',
    phone: '',
    avatar_url: '',
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        bio: user.bio || '',
        phone: user.phone || '',
        avatar_url: user.avatar_url || '',
      });
    }
  }, [user]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert({ type: 'error', message: 'Please select a valid image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({ type: 'error', message: 'Image size must be less than 5MB' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await apiService.uploadAdminAvatar(formData);
      
      setFormData(prev => ({
        ...prev,
        avatar_url: response.avatar_url,
      }));
      
      setAlert({ type: 'success', message: 'Avatar uploaded successfully!' });
    } catch (error) {
      console.error('Avatar upload error:', error);
      setAlert({ type: 'error', message: 'Failed to upload avatar' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      await dispatch(updateProfile(formData)).unwrap();
      setIsEditing(false);
      setAlert({ type: 'success', message: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Profile update error:', error);
      setAlert({ type: 'error', message: 'Failed to update profile' });
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        bio: user.bio || '',
        phone: user.phone || '',
        avatar_url: user.avatar_url || '',
      });
    }
    setIsEditing(false);
    setAlert(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'finance_admin': return 'warning';
      case 'product_admin': return 'info';
      case 'view_only': return 'default';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'finance_admin': return 'Finance Admin';
      case 'product_admin': return 'Product Admin';
      case 'view_only': return 'View Only';
      default: return role;
    }
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Profile Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your personal information and preferences
        </Typography>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
            <Typography variant="h6" component="h2">
              Personal Information
            </Typography>
            {!isEditing && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
              >
                Edit Profile
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            {/* Avatar Section */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box position="relative" mb={2}>
                  <Avatar
                    src={formData.avatar_url}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      fontSize: '2rem',
                      bgcolor: 'primary.main',
                    }}
                  >
                    {formData.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </Avatar>
                  
                  {isEditing && (
                    <Box position="absolute" bottom={0} right={0}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="avatar-upload"
                        type="file"
                        onChange={handleAvatarUpload}
                        disabled={uploading}
                      />
                      <label htmlFor="avatar-upload">
                        <IconButton
                          color="primary"
                          component="span"
                          disabled={uploading}
                          sx={{
                            bgcolor: 'background.paper',
                            border: 2,
                            borderColor: 'primary.main',
                            '&:hover': { bgcolor: 'primary.light' },
                          }}
                        >
                          <PhotoCameraIcon />
                        </IconButton>
                      </label>
                    </Box>
                  )}
                </Box>
                
                {uploading && (
                  <LinearProgress sx={{ width: '100%', mb: 1 }} />
                )}
                
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {isEditing ? 'Click camera to change avatar' : 'Profile Picture'}
                </Typography>
              </Box>
            </Grid>

            {/* Form Fields */}
            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!isEditing || isLoading}
                  InputProps={{
                    startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing || isLoading}
                  InputProps={{
                    startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="Bio"
                  multiline
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  disabled={!isEditing || isLoading}
                  placeholder="Tell us about yourself..."
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing || isLoading}
                  placeholder="+1 (555) 000-0000"
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          {isEditing && (
            <Box display="flex" gap={2} mt={3} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isLoading}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Account Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShieldIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Role & Permissions</Typography>
                </Box>
                <Chip
                  label={getRoleLabel(user.role)}
                  color={getRoleColor(user.role)}
                  size="small"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Last Login</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {user.last_login ? formatDate(user.last_login) : 'Never'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PersonIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Account Created</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user.created_at)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EditIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Last Updated</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user.updated_at)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfileSettings;